import type { RoundupTypeEnum } from '@repo/supergraph/base/enums';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { ALL_SLUG, ROUNDUP_FEATURES } from '../constants';
import type { FeatureFlagAudience } from '../types';

const FEATURE_FLAG_CONFIG = {
  ...ROUNDUP_FEATURES,
};

function getForcedFeatureFlag(feature: string): boolean | null {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);

    // Only allow URL-forcing for known flags to avoid enabling features for all known flags
    if (!FEATURE_FLAG_CONFIG[feature]) {
      return null;
    }

    const featureName = `feat_${feature}`;
    const featureValue = urlParams.get(featureName);

    if (featureValue === 'true') return true;
    if (featureValue === 'false') return false;
  }

  //feature flag wasn't forced
  return null;
}

type MatchesAudienceOpts = {
  audience: FeatureFlagAudience;
  slug: string;
  deviceType?: MobileOrDesktop;
  pageType?: RoundupTypeEnum;
};

function matchesAudience({ audience, slug, deviceType, pageType }: MatchesAudienceOpts): boolean {
  const isTargetDeviceType = !audience.targetDeviceType || audience.targetDeviceType === deviceType;
  const isTargetPageType = !audience.pageType || audience.pageType === pageType;
  const isTargetAudience = isTargetDeviceType && isTargetPageType;

  if (audience.slugs.includes(ALL_SLUG) && isTargetAudience) {
    return true;
  }

  if (audience.mode !== 'group') {
    return isTargetAudience && audience.slugs.includes(slug);
  }

  return audience.slugs.some(
    prefix => (slug === prefix || slug.startsWith(prefix)) && isTargetAudience,
  );
}

type Opts = {
  feature: string;
  slug: string;
  deviceType?: MobileOrDesktop;
  pageType?: RoundupTypeEnum;
};

export const isFeatureEnabled = ({ feature, slug, deviceType, pageType }: Opts): boolean => {
  const forcedFeatureFlag = getForcedFeatureFlag(feature);
  if (forcedFeatureFlag !== null) {
    return forcedFeatureFlag;
  }

  const flag = FEATURE_FLAG_CONFIG[feature];

  if (!flag) {
    return false;
  }

  return flag.audience.some(audience => matchesAudience({ audience, slug, deviceType, pageType }));
};
